import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, ReplaySubject, switchMap, tap, throwError } from 'rxjs';
import { User } from 'app/core/user/user.types';
import { environment } from 'environments/environment';
import { Company } from 'app/modules/models/company.type';
import { JwtHelperService } from '@auth0/angular-jwt';
import { UserPermission } from 'app/modules/models/profile-management/userPermission.type';
import { UserProfile, User_UserProfile } from 'app/modules/models/profile-management/userProfile.type';

interface TeraComponent {
    appComponentId?: string;
    link?: string;
    name?: string;
    title?: string;
    description?: string;
    isSica?: boolean;
    isActive?: boolean;
    isDeleted?: boolean;
}

interface TeraProfilePermission {
    id?: string;
    name?: string;
    userProfileId?: string;
    isActive?: boolean;
    isDeleted?: boolean;
    accessLevel?: string[] | string;
    teraComponent?: TeraComponent;
}

interface TeraUserProfile {
    id: string;
    name: string;
    description?: string;
    isActive?: boolean;
    isDeleted?: boolean;
    isSica?: boolean;
    sicaRoleCategory?: string;
    platform?: string;
}

interface TeraActorType {
    code?: string;
    name?: string;
}

interface TeraActor {
    id: string;
    numericId?: number;
    code?: string;
    name?: string;
    siret?: string;
    email?: string;
    phone?: string;
    actorTypeId?: string;
    actorType?: TeraActorType;
    isActive?: boolean;
    isDeleted?: boolean;
}

interface TeraUserProfileLink {
    id?: string;
    isActive?: boolean;
    isDeleted?: boolean;
    userId: string;
    userProfileId: string;
    userProfile?: TeraUserProfile;
}

interface TeraInternalUser {
    id: string;
    email: string;
    firstName?: string;
    name?: string;
    phone?: string;
    avatar?: string;
    isActive?: boolean;
    isDeleted?: boolean;
    canAccessSica?: boolean;
    actorId?: string;
    actor?: TeraActor;
    userProfiles?: TeraUserProfile[];
}

@Injectable({
    providedIn: 'root'
})
export class UserService {

    protected apiRoute = environment.RESOURCE_API;
    private readonly teraApiRoute = environment.TERA_RESOURCE_API;

    private _connectedUser: BehaviorSubject<User> = new BehaviorSubject<User>(null);
    private _selectedUser: ReplaySubject<User> = new ReplaySubject<User>(1);
    private _reload: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    private _isOnboarding: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
    private _selectedCompany: ReplaySubject<Company> = new ReplaySubject<Company>(1);
    lastCallTime: number;
    lastUser: any;

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for user
     *
     * @param value
     */
    set connectedUser(value: User) {
        // Store the value
        this._connectedUser.next(value);
    }

    get connectedUser$(): Observable<User> {
        return this._connectedUser.asObservable();
    }

    get selectedUser$(): Observable<User> {
        return this._selectedUser.asObservable();
    }

    set selectedUser(value: User) {
        // Store the value
        this._selectedUser.next(value);
    }

    get selectedCompany$(): Observable<Company> {
        return this._selectedCompany.asObservable();
    }

    set selectedCompany(value: Company) {
        // Store the value
        this._selectedCompany.next(value);
    }

    set reload(value: boolean) {
        this._reload.next(value);
    }

    get reload$(): Observable<boolean> {
        return this._reload.asObservable();
    }

    set isOnboarding(value: boolean) {
        this._isOnboarding.next(value);
    }

    get isOnboarding$(): Observable<boolean> {
        return this._isOnboarding.asObservable();
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get the current logged in user data
     */
    fetchUserByEmail(email: string): Observable<User> {
        const now = Date.now();

        if (now - this.lastCallTime < 10_000 && this.lastUser) {
            console.warn('API call blocked: returning cached value.');
            return of(this.lastUser);
        }

        this.lastCallTime = now;

        return this.loadAuthenticatedUser(email, false).pipe(
            tap((user) => {
                this._connectedUser.next(user);
                this.lastUser = user; // cache the value
            })
        );
    }

    signIn(email: string): Observable<User> {
        return this.loadAuthenticatedUser(email, true);
    }

    getUser(email: string): Observable<User> {
        return this.loadAuthenticatedUser(email, false);
    }

    /**
     * Identity remains the authentication authority. Except for SuperAdmin, the
     * application user, SICA profiles and page permissions are loaded from TERA.
     */
    private loadAuthenticatedUser(email: string, useLegacySignInRoute: boolean): Observable<User> {
        if (this.isSuperAdminToken()) {
            const legacyRoute = useLegacySignInRoute
                ? environment.RESOURCE_API + environment.apiCatalog.user.subRoutes.signIn + email
                : environment.RESOURCE_API + environment.apiCatalog.user.controllerRoute + email;
            return this._httpClient.get<User>(legacyRoute, this.prepareHeader());
        }

        return this._httpClient
            .get<TeraInternalUser>(
                `${this.teraApiRoute}api/User/GetSicaAccessByEmail/${encodeURIComponent(email)}`,
                this.prepareHeader()
            )
            .pipe(
                switchMap(teraUser => {
                    if (!teraUser) {
                        return throwError(() => ({
                            status: 403,
                            error: { message: "Utilisateur introuvable ou non autorise dans TERA." }
                        }));
                    }

                    const legacyRoute = useLegacySignInRoute
                        ? environment.RESOURCE_API + environment.apiCatalog.user.subRoutes.signIn + email
                        : environment.RESOURCE_API + environment.apiCatalog.user.controllerRoute + email;
                    const sicaAccount$ = this._httpClient
                        .get<User>(legacyRoute, this.prepareHeader())
                        .pipe(catchError(() => of(null as User)));

                    return forkJoin({
                        userProfiles: this.loadTeraSicaProfiles(teraUser),
                        sicaAccount: sicaAccount$
                    }).pipe(
                        map(({ userProfiles, sicaAccount }) => {
                            this.validateTeraSicaAccess(teraUser, userProfiles);
                            return this.mapTeraUser(teraUser, userProfiles, sicaAccount);
                        })
                    );
                })
            );
    }

    private loadTeraSicaProfiles(
        teraUser: TeraInternalUser
    ): Observable<Array<{ profile: TeraUserProfile; permissions: TeraProfilePermission[] }>> {
        const linksRoute =
            `${this.teraApiRoute}api/User_UserProfiles/GetAllByUserId/${teraUser.id}` +
            '?isTera=false&isSica=true';

        return this._httpClient.get<TeraUserProfileLink[]>(linksRoute, this.prepareHeader()).pipe(
            switchMap(links => {
                const uniqueLinks = Array.from(
                    new Map((links || []).map(link => [link.userProfileId, link])).values()
                ).filter(link =>
                    !!link.userProfileId &&
                    link.isActive !== false &&
                    link.isDeleted !== true
                );

                if (uniqueLinks.length === 0) {
                    return throwError(() => ({
                        status: 403,
                        error: { message: "Aucun profil SICA n'est attribue a cet utilisateur dans TERA." }
                    }));
                }

                return forkJoin(
                    uniqueLinks.map(link => {
                        const profile$ = link.userProfile
                            ? of(link.userProfile)
                            : this._httpClient.get<TeraUserProfile>(
                                `${this.teraApiRoute}api/UserProfile/GetById/${link.userProfileId}`,
                                this.prepareHeader()
                            );
                        const permissions$ = this._httpClient.get<TeraProfilePermission[]>(
                            `${this.teraApiRoute}api/UserProfilePermission/GetByProfileId/${link.userProfileId}`,
                            this.prepareHeader()
                        );

                        return forkJoin({ profile: profile$, permissions: permissions$ });
                    })
                );
            }),
            map(profiles => profiles.filter(item =>
                item.profile?.isActive !== false && item.profile?.isDeleted !== true
            )),
            switchMap(profiles => profiles.length > 0
                ? of(profiles)
                : throwError(() => ({
                    status: 403,
                    error: { message: "Aucun profil SICA actif n'est attribue a cet utilisateur dans TERA." }
                })))
        );
    }

    private mapTeraUser(
        teraUser: TeraInternalUser,
        teraProfiles: Array<{ profile: TeraUserProfile; permissions: TeraProfilePermission[] }>,
        sicaAccount?: User
    ): User {
        const userUserProfiles: User_UserProfile[] = teraProfiles.map(item => {
            const profile: UserProfile = {
                id: item.profile.id,
                name: item.profile.name,
                description: item.profile.description || '',
                companyId: null,
                permissions: (item.permissions || [])
                    .filter(permission =>
                        permission.isActive !== false &&
                        permission.isDeleted !== true &&
                        permission.teraComponent?.isActive !== false &&
                        permission.teraComponent?.isDeleted !== true &&
                        permission.teraComponent?.isSica !== false &&
                        this.hasAccessLevel(permission.accessLevel)
                    )
                    .map(permission => this.mapTeraPermission(permission))
            };

            return {
                userId: teraUser.id,
                userProfileId: item.profile.id,
                userProfile: profile
            };
        });

        return {
            ...(sicaAccount || {}),
            id: teraUser.id,
            email: teraUser.email,
            firstname: teraUser.firstName || '',
            lastname: teraUser.name || '',
            fullname: `${teraUser.firstName || ''} ${teraUser.name || ''}`.trim() || teraUser.email,
            avatar: teraUser.avatar,
            phone: teraUser.phone || '',
            enabled: teraUser.isActive !== false,
            // TERA owns profiles/permissions, while the SICA account remains the
            // authority for onboarding, CGU and company identity information.
            cguAccepted: sicaAccount?.cguAccepted ?? true,
            cguAcceptedOn: sicaAccount?.cguAcceptedOn,
            company: sicaAccount?.company,
            companyId: sicaAccount?.companyId,
            hasCrmAccess: true,
            hasEPAccess: true,
            teraActorId: teraUser.actorId,
            teraActor: teraUser.actor,
            userProfiles: userUserProfiles.map(link => link.userProfile),
            user_UserProfiles: userUserProfiles
        } as User;
    }

    private validateTeraSicaAccess(
        teraUser: TeraInternalUser,
        profiles: Array<{ profile: TeraUserProfile; permissions: TeraProfilePermission[] }>
    ): void {
        if (teraUser.isActive === false || teraUser.isDeleted === true || teraUser.canAccessSica === false) {
            throw this.forbidden("Cet utilisateur TERA n'est pas actif ou ne possede pas d'acces SICA.");
        }

        if (!teraUser.actorId) {
            return;
        }

        const actor = teraUser.actor;
        if (!actor || actor.isActive === false || actor.isDeleted === true) {
            throw this.forbidden("L'acteur rattache a cet utilisateur n'est pas actif.");
        }

        const expectedFamilyByActorType: Record<string, string> = {
            AC2: 'Partenaire',
            AC6: 'Mandataire',
            AC7: 'Superviseur'
        };
        const actorTypeCode = (actor.actorType?.code || '').trim().toUpperCase();
        const expectedFamily = expectedFamilyByActorType[actorTypeCode];
        if (!expectedFamily) {
            throw this.forbidden("Seuls les contacts partenaire, mandataire et superviseur peuvent acceder a SICA.");
        }

        const hasMatchingProfile = profiles.some(item =>
            this.getSicaProfileFamily(item.profile) === expectedFamily
        );
        if (!hasMatchingProfile) {
            throw this.forbidden("Le profil SICA du contact ne correspond pas au type de son acteur.");
        }
    }

    private getSicaProfileFamily(profile: TeraUserProfile): string | null {
        const requiredFamilies: Record<string, string> = {
            'mandataire': 'Mandataire',
            'superviseur': 'Superviseur',
            'mandataire projets': 'Mandataire',
            'mandataire administratif projets': 'Mandataire',
            'mandataire administratif': 'Mandataire',
            'partenaire acep projets': 'Partenaire',
            'partenaire acep': 'Partenaire'
        };
        const profileName = (profile?.name || '').trim().toLowerCase();
        return requiredFamilies[profileName] || profile?.sicaRoleCategory?.trim() || null;
    }

    private forbidden(message: string): any {
        return {
            status: 403,
            error: { message }
        };
    }

    private mapTeraPermission(permission: TeraProfilePermission): UserPermission {
        const component = permission.teraComponent || {};
        const pageRoute = this.normalizePageRoute(component.link || component.appComponentId || '');

        return {
            id: permission.id,
            name: permission.name || component.title || component.name || pageRoute,
            description: component.description || '',
            type: 'page',
            page: component.title || component.name || pageRoute,
            pageRoute,
            userProfileId: permission.userProfileId
        };
    }

    private normalizePageRoute(route: string): string {
        const normalized = (route || '').trim().split('?')[0].split('#')[0].replace(/^\/+|\/+$/g, '');
        return normalized ? `/${normalized}` : '';
    }

    private hasAccessLevel(accessLevel: string[] | string | undefined): boolean {
        if (Array.isArray(accessLevel)) {
            return accessLevel.length > 0;
        }
        if (typeof accessLevel !== 'string' || !accessLevel.trim()) {
            return false;
        }

        try {
            const parsed = JSON.parse(accessLevel);
            return Array.isArray(parsed) ? parsed.length > 0 : !!parsed;
        } catch {
            return true;
        }
    }

    private isSuperAdminToken(): boolean {
        const token = localStorage.getItem('accessToken');
        if (!token) {
            return false;
        }

        const decodedToken = new JwtHelperService().decodeToken(token) || {};
        const role = decodedToken.role ??
            decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
        return Array.isArray(role) ? role.includes('SuperAdmin') : role === 'SuperAdmin';
    }

    /**
     * Update the user
     *
     * @param user
     */
    update(user: User): Observable<any> {
        let route = this.apiRoute + environment.apiCatalog.user.subRoutes.update;
        return this._httpClient.put<User>(route, user, this.prepareHeader()).pipe(
            map((response) => {
                this._connectedUser.next(response);
            })
        );
    }

    activate(user: User): Observable<any> {
        let route = this.apiRoute + environment.apiCatalog.user.subRoutes.activate;
        return this._httpClient.post<User>(route, user, this.prepareHeader()).pipe(
            map((response) => {
                this._connectedUser.next(response);
            })
        );
    }

    getById(id: string): Observable<User> {
        const route = this.apiRoute + environment.apiCatalog.user.controllerRoute + '/' + id;
        return this._httpClient.get<User>(route, this.prepareHeader());
    }

    getUsers(): Observable<User[]> {
        const route = this.apiRoute + environment.apiCatalog.user.subRoutes.getAll;
        return this._httpClient.get<User[]>(route, this.prepareHeader());
    }

    getCompanyUsers(companyId: string): Observable<User[]> {
        const route = this.apiRoute + environment.apiCatalog.user.subRoutes.getAll + '/' + companyId;
        return this._httpClient.get<User[]>(route, this.prepareHeader());
    }

    getCountByCompanyId(companyId: string): Observable<number> {
        const route = this.apiRoute + environment.apiCatalog.user.subRoutes.getCount + '/' + companyId;
        return this._httpClient.get<number>(route, this.prepareHeader());
    }

    getCountAll(): Observable<number> {
        const route = this.apiRoute + environment.apiCatalog.user.subRoutes.getCountAllUsers;
        return this._httpClient.get<number>(route, this.prepareHeader());
    }

    public createUser(body: User): Observable<User> {
        const route = this.apiRoute + environment.apiCatalog.user.controllerRoute;
        return this._httpClient.post(route, body, this.prepareHeader()).pipe(map(res => res as any));
    }

    deleteUser(body: User): Observable<any> {
        let headers = new HttpHeaders();
        headers = headers.set('Accept', 'application/json');
        const route = this.apiRoute + environment.apiCatalog.user.subRoutes.delete;
        return this._httpClient.delete(route, { headers: headers, body: body }).pipe(map(res => res as any))
    }

    updateUser(user: User): Observable<any> {
        let route = this.apiRoute + environment.apiCatalog.user.subRoutes.update;
        return this._httpClient.put<User>(route, user, this.prepareHeader()).pipe(map(res => res as any))
    }

    getUsersByProfileId(profileId: string): Observable<User[]> {
        const route = this.apiRoute + environment.apiCatalog.user.subRoutes.getAll + '/' + profileId;
        return this._httpClient.get<User[]>(route, this.prepareHeader());
    }

    deactivateUser(user: User): Observable<any> {
        let route = this.apiRoute + environment.apiCatalog.user.subRoutes.deactivate;
        return this._httpClient.put<User>(route, user, this.prepareHeader()).pipe(map(res => res as any))
    }

    cloneDatabase(action: string): Observable<any> {
        const formData = new FormData();
        formData.append('action', action);
        let route = this.apiRoute + environment.apiCatalog.user.subRoutes.cloneDatabase + '?action=' + action;

        return this._httpClient.post<any>(route, formData, this.prepareHeader()).pipe(map(res => res as any))

    }
    changeUserCrmPartenerStatus(userid: string, status): Observable<any> {
        const route = this.apiRoute + environment.apiCatalog.user.subRoutes.changeUserCrmPartenerStatus + userid + "/" + status;
        return this._httpClient.get<any>(route, this.prepareHeader());
    }
    changeUserEpPartenerStatus(userid: string, status): Observable<any> {
        const route = this.apiRoute + environment.apiCatalog.user.subRoutes.changeUserEpPartenerStatus + userid + "/" + status;
        return this._httpClient.get<any>(route, this.prepareHeader());
    }

    private prepareHeader(): Object {
        let headers = new HttpHeaders();
        headers = headers.set('Accept', 'application/json');
        return { headers: headers };
    }
}
